import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { isImageMimeType, isPdf } from 'src/shared/utils/fileTypeUtils';

import {
  cn,
  HeroModal as Modal,
  ModalBody,
  ModalContent
} from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

import { FilePreview } from './FilePreview';
import { ImagePreview } from './ImagePreview';

/**
 * Represents a media item (image or file) in the carousel
 */
export interface MediaItem {
  id: string;
  url: string;
  name: string;
  content_type: string;
  caption?: string;
}

interface MediaCarouselModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: MediaItem[];
  currentIndex: number;
  onNext: () => void;
  onPrevious: () => void;
  onIndexChange: (index: number) => void;
}

/**
 * Full-screen modal component for viewing images and files in a carousel.
 * Uses ImagePreview for images and FilePreview for other file types.
 */
export function MediaCarouselModal({
  isOpen,
  onClose,
  items,
  currentIndex,
  onNext,
  onPrevious,
  onIndexChange
}: MediaCarouselModalProps): React.ReactElement | null {
  const currentItem = items[currentIndex];
  const hasMultipleItems = items.length > 1;

  // Handle keyboard navigation with wrap-around
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [isOpen, onPrevious, onNext, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!currentItem) return null;

  const isCurrentItemImage = isImageMimeType(currentItem.content_type);
  const isCurrentItemPdf = isPdf(currentItem.content_type, currentItem.name);

  // Animation variants - opacity only to prevent layout shift
  const fadeVariants = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  };

  return (
    <Modal
      backdrop="blur"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      placement="center"
      size="full"
      classNames={{
        wrapper: 'z-[9999]',
        backdrop: 'z-[9998] bg-[#F9FAFB66]/40 backdrop-blur-md'
      }}
      hideCloseButton
    >
      <ModalContent className="bg-transparent shadow-none h-full">
        <ModalBody className="p-6 flex flex-col h-full relative overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 hover:bg-black/10 rounded-full transition-colors z-10"
            aria-label="Close"
          >
            <XMarkIcon className="size-8 text-gray-700" />
          </button>

          {/* Media Preview Area - takes remaining space */}
          <div className="flex-1 flex items-center justify-center w-full max-w-[95vw] mx-auto min-h-0">
            {/* Media content with animation */}
            <div className="h-full w-full flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${currentItem.id}-${currentIndex}`}
                  variants={fadeVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{
                    duration: 0.15,
                    ease: 'easeInOut'
                  }}
                  className="h-full w-full flex items-center justify-center"
                >
                  {isCurrentItemImage ? (
                    <ImagePreview
                      src={currentItem.url}
                      alt={currentItem.name || 'Image preview'}
                      className="max-h-[80%] max-w-full h-auto w-auto object-contain"
                    />
                  ) : (
                    <FilePreview
                      url={currentItem.url}
                      name={currentItem.name}
                      contentType={currentItem.content_type}
                      className={
                        isCurrentItemPdf
                          ? 'h-full w-[90%]'
                          : 'max-h-full max-w-[600px] w-full'
                      }
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Footer - fixed height section */}
          <div className="flex-shrink-0 flex flex-col items-center gap-2 pt-2">
            {/* File name and caption */}
            <div className="flex flex-col items-center gap-0.5">
              <Typography
                variant="body-2"
                weight="medium"
                className="text-gray-600 truncate max-w-md"
              >
                {currentItem.name}
              </Typography>
              {currentItem.caption && (
                <Typography
                  variant="body-3"
                  className="text-secondary-text text-center max-w-md"
                >
                  {currentItem.caption}
                </Typography>
              )}
            </div>

            {/* Thumbnails with navigation */}
            {hasMultipleItems && (
              <div className="flex gap-3 items-center justify-center max-w-full px-4 py-2">
                {/* Previous button */}
                <button
                  onClick={onPrevious}
                  className="flex-shrink-0 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeftIcon className="size-5 text-gray-700" />
                </button>

                {/* Thumbnails */}
                <div className="flex gap-2 items-center justify-center overflow-x-auto p-5">
                  {items.map((item, index) => {
                    const isSelected = index === currentIndex;
                    const isItemImage = isImageMimeType(item.content_type);

                    return (
                      <button
                        key={item.id}
                        onClick={() => onIndexChange(index)}
                        className={cn(
                          'flex-shrink-0 size-14 rounded-lg overflow-hidden transition-all duration-200 border border-gray-4',
                          isSelected
                            ? 'shadow-[0_0_12px_#000eef80] border-transparent'
                            : 'opacity-60 hover:opacity-100'
                        )}
                        aria-label={`View ${item.name}`}
                      >
                        {isItemImage ? (
                          <img
                            src={item.url}
                            alt={item.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className="size-full bg-gray-2 flex items-center justify-center">
                            <DocumentIcon className="size-6 text-gray-500" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Next button */}
                <button
                  onClick={onNext}
                  className="flex-shrink-0 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
                  aria-label="Next"
                >
                  <ChevronRightIcon className="size-5 text-gray-700" />
                </button>
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
